import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { blogsAPI } from '../../lib/supabase';
import './Blog.css';

const Blog = () => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [blogPosts, setBlogPosts] = useState([]);

  // Load blog posts from Supabase database
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const blogs = await blogsAPI.getAll();
        
        // Transform Supabase blog format to match blog page format
        const transformedBlogs = blogs.map(blog => ({
          id: blog.id,
          title: blog.title,
          slug: blog.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
          excerpt: blog.description,
          content: blog.content || 'Full article content...',
          image: blog.image || 'https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          category: 'solar-technology',
          publishDate: blog.publish_date,
          readTime: '5 min read',
          tags: ['Solar Energy', 'News', 'Updates'],
          featured: false
        }));
        
        setBlogPosts(transformedBlogs);
        console.log('Blogs loaded from Supabase:', transformedBlogs.length);
      } catch (error) {
        console.error('Error fetching blogs from Supabase:', error);
        setBlogPosts([]); // Set empty array if fetch fails
      }
    };

    fetchBlogs();
  }, []);

  const categories = [
    { key: 'all', label: 'All Posts' },
    { key: 'solar-technology', label: 'Solar Technology' },
    { key: 'residential-solar', label: 'Residential Solar' },
    { key: 'commercial-solar', label: 'Commercial Solar' },
    { key: 'solar-economics', label: 'Solar Economics' },
    { key: 'maintenance', label: 'Maintenance' },
    { key: 'energy-comparison', label: 'Energy Comparison' },
    { key: 'policy-incentives', label: 'Policy & Incentives' },
    { key: 'energy-storage', label: 'Energy Storage' }
  ];

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = filter === 'all' || post.category === filter;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const featuredPosts = blogPosts.filter(post => post.featured);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <>
      <Helmet>
        <title>Solar Energy Blog - Latest Insights and Industry News</title>
        <meta name="description" content="Stay updated with the latest trends, insights, and best practices in solar energy, renewable technology, and sustainable living." />
        <meta name="keywords" content="solar energy blog, renewable energy, solar technology, solar panels, sustainable energy, solar news" />
      </Helmet>

      <div className="blog-page">
        {/* Hero Section */}
        <section className="blog-hero" style={{backgroundImage: `url('/images/Hero%20image/Blogpag.jpeg')`}}>
          <div className="blog-hero-overlay"></div>
          <div className="blog-hero-content">
            <h1 className="blog-hero-title">Blog</h1>
          </div>
        </section>



        {/* All Posts */}
        <section className="section all-posts">
          <div className="container">
            <div className="section-header text-center">
              <h2>All Articles</h2>
              <p>
                {filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''} found
                {searchTerm && ` for "${searchTerm}"`}
                {filter !== 'all' && ` in ${categories.find(cat => cat.key === filter)?.label}`}
              </p>
            </div>
            <div className="posts-grid">
              {filteredPosts.map((post) => (
                <article key={post.id} className="post-card">
                  <div className="post-image">
                    <img src={post.image} alt={post.title} loading="lazy" />
                  </div>
                  <div className="post-content">
                    <div className="post-meta">
                      <div className="post-date">{formatDate(post.publishDate)}</div>
                    </div>
                    <h3 className="post-title">
                      <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                    </h3>
                    <div className="post-footer">
                      <Link to={`/blog/${post.slug}`} className="read-more">
                        Read More
                        <i className="fas fa-arrow-right"></i>
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {filteredPosts.length === 0 && (
              <div className="no-posts">
                <h3>No articles found</h3>
                <p>Try adjusting your search terms or filters to find what you're looking for.</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    setSearchTerm('');
                    setFilter('all');
                  }}
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </section>

      </div>
    </>
  );
};

export default Blog;